import { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { Article } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { getArticleInfo, getSetOfArticles } from '@prismicVitals';

/**
 * Custom React hook to fetch articles based on provided article IDs or default criteria.
 * Accepts either a comma-separated string or an array of article IDs.
 * Utilizes the `Context` to access global state (e.g., the user's country) and the current URL path
 * from `useLocation` to determine conditional logic in fetching articles.
 *
 * @param articleIds Optional comma-separated string or an array of article IDs to fetch specific articles.
 * If no IDs are provided, the hook defaults to fetching articles based on general criteria,
 * such as excluding certain articles if the current country is 'US' or the path includes 'landing'.
 *
 * @param fixedAmount Optional number of total articles to return. If provided and greater than the number
 * of articleIds, additional articles will be fetched to reach the desired count.
 *
 * @returns An array of `Article` objects as fetched either by specific IDs or general criteria.
 * This state is managed internally within the hook and updates whenever the article IDs, country,
 * or path changes, triggering a re-fetch.
 *
 * Usage example:
 * const articles = useArticles("123,456,789");
 * or
 * const articles = useArticles(["123", "456", "789"]);
 * This will fetch articles with IDs 123, 456, and 789 if specified; otherwise, it fetches based on default logic.
 */
export const useArticles = (articleIds?: string | string[] | null, fixedAmount?: number): Article[] => {
  const location = useLocation();
  const { country } = useCountry();
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    const hideCBD = country === 'US' || location.pathname.includes('landing');
    let idsArray: string[] = [];

    if (articleIds) {
      if (Array.isArray(articleIds)) {
        idsArray = articleIds;
      } else {
        idsArray = articleIds.split(',');
      }

      if (fixedAmount && fixedAmount > idsArray.length) {
        setIsLoadingMore(true);
        getSetOfArticles(
          (specificArticles: Article[]) => {
            setArticles(specificArticles);
            const numberToFill = fixedAmount - idsArray.length;
            getArticleInfo(
              (additionalArticles: Article[]) => {
                setArticles((prev) => {
                  const newArticles = [...prev];
                  // Filter duplicates
                  const uniqueAdditional = additionalArticles.filter((article) => !prev.some((prevArticle) => prevArticle.id === article.id));
                  newArticles.push(...uniqueAdditional.slice(0, numberToFill));
                  setIsLoadingMore(false);
                  return newArticles;
                });
              },
              numberToFill,
              hideCBD
            );
          },
          idsArray.length,
          idsArray
        );
      } else {
        getSetOfArticles(setArticles, idsArray.length, idsArray);
      }
    } else {
      const numberOfArticles = fixedAmount || 9;
      getArticleInfo(setArticles, numberOfArticles, hideCBD);
    }
  }, [articleIds, fixedAmount, country, location.pathname]);

  return isLoadingMore ? [] : articles;
};
